import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { AlertTriangle, Clock, TrendingDown, Search, Users, FileText, Activity, Zap, Target, Calendar as CalendarIcon } from 'lucide-react';
import { KPICard, WidgetCard, Pill, PrimaryButton } from '@/components/ui/native';
import TargetsTab from '@/components/manage/TargetsTab';

const TABS = [
  { id: 'attention', label: 'Needs Attention', icon: AlertTriangle },
  { id: 'targets', label: 'Targets', icon: Target },
  { id: 'reports', label: 'Manager Reports', icon: FileText },
  { id: 'search', label: 'Org-wide Search', icon: Search },
  { id: 'coaching', label: 'Coaching', icon: Users },
  { id: 'delegations', label: 'Delegations', icon: Activity },
];

// ─── Currency formatter (INR) ─────────────────────────────────────────────
function fmtINR(n) {
  if (n == null) return '—';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
}

function timeAgo(iso) {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

// ─── Lightweight inline sparkline (SVG, no external dep) ──────────────────
function Sparkline({ data, color = '#0f172a' }) {
  if (!data?.length) return null;
  const w = 80, h = 24, pad = 2;
  const max = Math.max(...data.map((d) => d.count), 1);
  const step = (w - pad * 2) / Math.max(1, data.length - 1);
  const points = data.map((d, i) => {
    const x = pad + i * step;
    const y = h - pad - ((d.count / max) * (h - pad * 2));
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="opacity-80">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      {/* Last point highlighted */}
      {data.length > 0 && (() => {
        const last = points.split(' ').pop().split(',');
        return <circle cx={last[0]} cy={last[1]} r="2" fill={color} />;
      })()}
    </svg>
  );
}

// ─── Pulse Gauge (semicircle 0-100) ───────────────────────────────────────
function PulseGauge({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const tone = v >= 75 ? 'text-emerald-500' : v >= 50 ? 'text-amber-500' : 'text-rose-500';
  const label = v >= 75 ? 'Strong' : v >= 50 ? 'Steady' : 'Needs attention';
  // Semi-circle: 180° arc, mapped to 100 points
  const angle = (v / 100) * 180;
  const radius = 60, cx = 80, cy = 70;
  const x = cx + radius * Math.cos(Math.PI * (180 - angle) / 180);
  const y = cy - radius * Math.sin(Math.PI * (180 - angle) / 180);
  const largeArc = angle > 180 ? 1 : 0;
  const arcPath = `M ${cx - radius} ${cy} A ${radius} ${radius} 0 ${largeArc} 1 ${x} ${y}`;
  return (
    <div className="flex flex-col items-center">
      <svg width="160" height="90" viewBox="0 0 160 90">
        <path d={`M 20 70 A 60 60 0 0 1 140 70`} fill="none" stroke="currentColor" strokeWidth="10" className="text-slate-200 dark:text-slate-600" strokeLinecap="round" />
        <path d={arcPath} fill="none" strokeWidth="10" stroke="currentColor" className={tone} strokeLinecap="round" />
        <text x="80" y="62" textAnchor="middle" className="fill-slate-900 dark:fill-white font-bold" style={{ fontSize: '26px' }}>{v}</text>
      </svg>
      <span className={`text-xs font-semibold ${tone} -mt-2`}>{label}</span>
    </div>
  );
}

function AttentionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, u] = await Promise.all([
          axios.get(`${API}/manage/attention`),
          axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
        ]);
        setData(r.data); setUser(u.data);
      } catch { setData(null); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6 text-slate-400">Loading…</div>;
  if (!data) return <div className="p-6 text-slate-400">No data</div>;

  // Prioritized alert list
  const alerts = [];
  if (data.deals_at_risk_inr > 0) alerts.push({
    severity: 'high', icon: AlertTriangle,
    title: `${fmtINR(data.deals_at_risk_inr)} of pipeline going cold`,
    detail: `${data.stuck_pipeline?.length || 0} deals haven't moved in 14+ days`,
  });
  if ((data.idle_reps?.length || 0) >= 3) alerts.push({
    severity: 'med', icon: Clock,
    title: `${data.idle_reps.length} reps haven't logged any activity for 5 days`,
    detail: 'They may need a 1:1 or coaching nudge',
  });
  if ((data.downtrending?.length || 0) > 0) alerts.push({
    severity: 'med', icon: TrendingDown,
    title: `${data.downtrending.length} reps trending down week-over-week`,
    detail: 'Activity dropped 30%+ vs last week',
  });
  if (data.unassigned_leads > 0) alerts.push({
    severity: 'low', icon: Users,
    title: `${data.unassigned_leads} unassigned leads in queue`,
    detail: 'Round-robin them to balance load',
  });

  const greeting = (() => {
    const hr = new Date().getHours();
    if (hr < 12) return 'Good morning';
    if (hr < 17) return 'Good afternoon';
    return 'Good evening';
  })();
  const firstName = (user?.name || '').split(' ')[0] || '';

  return (
    <div className="space-y-6">
      {/* ── HERO BRIEFING ─────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        {/* Left accent stripe (red/amber/emerald based on pulse) */}
        <div
          aria-hidden
          className={`absolute inset-y-0 left-0 w-1 ${
            (data.team_pulse ?? 0) >= 75 ? 'bg-emerald-500'
              : (data.team_pulse ?? 0) >= 50 ? 'bg-amber-500'
              : 'bg-rose-500'
          }`}
        />
        <div className="flex items-center justify-between gap-6 flex-wrap p-6 pl-7">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 mb-2">
              Today's briefing
            </div>
            <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-1.5">
              {greeting}{firstName ? `, ${firstName}` : ''}.
            </h2>
            <p className="text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed">
              {alerts.length === 0
                ? `Your team of ${data.team_size} is firing on all cylinders today.`
                : alerts.length === 1
                  ? alerts[0].title
                  : `You have ${alerts.length} things to look at — starting with ${alerts[0].title.toLowerCase()}.`}
            </p>
            <div className="flex items-center gap-3 mt-4 text-xs text-slate-500 dark:text-slate-400 flex-wrap">
              <span className="inline-flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" />
                {data.team_size} reports
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarIcon className="w-3.5 h-3.5" />
                {data.daily_trend?.reduce((a, b) => a + b.count, 0) || 0} activities this week
              </span>
              <span className="text-slate-300 dark:text-slate-600">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5" />
                refreshed {timeAgo(data.generated_at)}
              </span>
            </div>
          </div>
          <div className="shrink-0">
            <div className="bg-slate-50 dark:bg-slate-900/60 rounded-lg px-5 py-3 border border-slate-200 dark:border-slate-700">
              <PulseGauge value={data.team_pulse} />
              <div className="text-[10px] uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400 text-center mt-1.5 font-semibold">
                Team Pulse
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI ROW WITH SPARKLINE ────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Pipeline at risk</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{fmtINR(data.deals_at_risk_inr)}</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{data.stuck_pipeline?.length || 0} deals · 14d+ stuck</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Idle reps</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.idle_reps?.length || 0}</span>
            <span className="text-sm text-slate-400">/ {data.team_size}</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">no activity 5d+</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Trending down</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">{data.downtrending?.length || 0}</div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">activity ↓30% WoW</p>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Activity (7d)</span>
            <Sparkline data={data.daily_trend || []} />
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {data.daily_trend?.reduce((a, b) => a + b.count, 0) || 0}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">team activities</p>
        </div>
      </div>

      {/* ── PRIORITY ALERTS ───────────────────────────────────────────── */}
      {alerts.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Priority alerts</span>
            <span className="text-xs text-slate-400">{alerts.length} item{alerts.length > 1 ? 's' : ''}</span>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {alerts.map((a, i) => {
              const Icon = a.icon;
              const sev = a.severity === 'high' ? 'rose' : a.severity === 'med' ? 'amber' : 'slate';
              const sevColor = sev === 'rose' ? 'bg-rose-50 text-rose-600 dark:bg-rose-900/30' : sev === 'amber' ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/30' : 'bg-slate-50 text-slate-500 dark:bg-slate-700';
              return (
                <div key={i} className="px-4 py-3 flex items-start gap-3" data-testid={`alert-${a.severity}`}>
                  <div className={`w-9 h-9 rounded-lg ${sevColor} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200">{a.title}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{a.detail}</div>
                  </div>
                  <Pill tone={sev}>{a.severity.toUpperCase()}</Pill>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── 2-col: Idle reps + Stuck pipeline ─────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Idle reps · 5d+ no activity</span>
          </div>
          {data.idle_reps?.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">All reps active 🎉</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[380px] overflow-y-auto">
              {data.idle_reps.slice(0, 30).map((r) => (
                <div key={r.user_id} className="px-4 py-2.5 flex items-center justify-between" data-testid="idle-reps-card">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{r.name}</div>
                    <div className="text-xs text-slate-500">{r.role} · last seen {timeAgo(r.last_activity_at)}</div>
                  </div>
                  <Pill tone={r.priority === 'high' ? 'rose' : 'amber'}>{r.priority === 'high' ? '🔴 cold' : '🟡 idle'}</Pill>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Stuck pipeline · 14d+ unmoved</span>
            <span className="text-xs font-bold text-rose-600">{fmtINR(data.deals_at_risk_inr)}</span>
          </div>
          {data.stuck_pipeline?.length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">Nothing stuck. All deals are moving.</div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-[380px] overflow-y-auto" data-testid="stuck-pipeline-card">
              {data.stuck_pipeline.slice(0, 20).map((o) => (
                <a key={o.id} href={`/opportunities/${o.id}`} className="px-4 py-2.5 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-700/30">
                  <div className="min-w-0 flex-1 mr-3">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{o.name}</div>
                    <div className="text-xs text-slate-500 truncate">{o.owner_name} · stuck since {timeAgo(o.updated_at)}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">{fmtINR(o.deal_value)}</div>
                    <Pill tone="amber">{o.stage}</Pill>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── RECENT ACTIVITY TICKER ─────────────────────────────────────── */}
      {data.recent_activity?.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-700">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Live · recent team movements</span>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {data.recent_activity.map((a, idx) => {
              const isWin = a.stage === 'closed_won';
              const isLoss = a.stage === 'closed_lost';
              const dot = isWin ? 'bg-emerald-500' : isLoss ? 'bg-rose-500' : 'bg-blue-500';
              return (
                <div key={idx} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                  <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`}></span>
                  <span className="font-medium text-slate-700 dark:text-slate-200">{a.actor_name}</span>
                  <span className="text-slate-500">{a.verb}</span>
                  <span className="font-medium text-slate-800 dark:text-slate-100 truncate flex-1">{a.deal_name}</span>
                  {a.deal_value > 0 && <span className="text-slate-600 dark:text-slate-400 font-semibold flex-shrink-0">{fmtINR(a.deal_value)}</span>}
                  <span className="text-xs text-slate-400 flex-shrink-0">{timeAgo(a.ts)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ReportsTab() {
  const [funnel, setFunnel] = useState(null);
  const [activity, setActivity] = useState(null);
  const [tenure, setTenure] = useState(null);
  useEffect(() => {
    (async () => {
      const [f, a, t] = await Promise.all([
        axios.get(`${API}/manage/reports/funnel`).catch(() => ({ data: { reps: [] } })),
        axios.get(`${API}/manage/reports/activity-ratio?days=30`).catch(() => ({ data: { reps: [] } })),
        axios.get(`${API}/manage/reports/tenure`).catch(() => ({ data: {} })),
      ]);
      setFunnel(f.data); setActivity(a.data); setTenure(t.data);
    })();
  }, []);
  return (
    <div className="space-y-6">
      <WidgetCard title="Funnel conversion by rep">
        <table className="w-full text-sm -mx-4">
          <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Rep</th>
              <th className="px-3 py-2">New</th>
              <th className="px-3 py-2">Contacted</th>
              <th className="px-3 py-2">Qualified</th>
              <th className="px-3 py-2">Won</th>
              <th className="px-3 py-2">Lost</th>
              <th className="px-3 py-2">Win rate</th>
            </tr>
          </thead>
          <tbody>
            {(funnel?.reps || []).slice(0, 15).map((r) => (
              <tr key={r.user_id} className="border-t border-slate-100 dark:border-slate-700">
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{r.name}</td>
                <td className="px-3 py-2 text-center">{r.new}</td>
                <td className="px-3 py-2 text-center">{r.contacted}</td>
                <td className="px-3 py-2 text-center">{r.qualified}</td>
                <td className="px-3 py-2 text-center text-emerald-600 font-semibold">{r.won}</td>
                <td className="px-3 py-2 text-center text-rose-500">{r.lost}</td>
                <td className="px-3 py-2 text-center font-semibold">{r.win_rate}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </WidgetCard>

      <WidgetCard title="Activity-to-pipeline ratio · last 30 days">
        <table className="w-full text-sm -mx-4">
          <thead className="bg-slate-50 dark:bg-slate-900 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Rep</th>
              <th className="px-3 py-2">Activities</th>
              <th className="px-3 py-2">Per day</th>
              <th className="px-3 py-2">Won</th>
              <th className="px-3 py-2">Acts/win</th>
            </tr>
          </thead>
          <tbody>
            {(activity?.reps || []).slice(0, 15).map((r) => (
              <tr key={r.user_id} className="border-t border-slate-100 dark:border-slate-700">
                <td className="px-3 py-2 text-slate-700 dark:text-slate-300">{r.name}</td>
                <td className="px-3 py-2 text-center">{r.activities}</td>
                <td className="px-3 py-2 text-center">{r.activities_per_day}</td>
                <td className="px-3 py-2 text-center font-semibold">{r.deals_won}</td>
                <td className="px-3 py-2 text-center text-slate-500">{r.ratio == null ? '—' : r.ratio}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </WidgetCard>

      {tenure && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
          <KPICard title="New hires (<6mo)" value={`${tenure?.new_hires?.reps || 0} reps`} unit={`avg ${tenure?.new_hires?.avg_won || 0} wins`} />
          <KPICard title="Tenured (6mo+)" value={`${tenure?.tenured?.reps || 0} reps`} unit={`avg ${tenure?.tenured?.avg_won || 0} wins`} />
        </div>
      )}
    </div>
  );
}

function SearchTab() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = async () => {
    if (q.trim().length < 2) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/manage/search`, { params: { q: q.trim() } });
      setResults(r.data);
    } catch (e) { setResults(null); }
    finally { setLoading(false); }
  };
  return (
    <div className="space-y-4">
      <WidgetCard title="Search across all collections" headerRight={<></>}>
        <div className="flex gap-2 -mx-4 px-4">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && run()}
            placeholder="Type a name, email, organization…"
            className="flex-1 px-4 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200"
            data-testid="org-search-input"
          />
          <PrimaryButton onClick={run} data-testid="org-search-go">Search</PrimaryButton>
        </div>
      </WidgetCard>

      {loading && <div className="text-slate-400 px-4">Searching…</div>}

      {results && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {['leads', 'opportunities', 'contacts', 'companies'].map((k) => (
            <WidgetCard
              key={k}
              title={k}
              headerRight={<span className="text-xs text-slate-400 normal-case">{results[k]?.length || 0}</span>}
            >
              <div className="divide-y divide-slate-100 dark:divide-slate-700 text-sm -mx-4">
                {(results[k] || []).slice(0, 8).map((row) => (
                  <a key={row.id} href={`/${k}/${row.id}`} className="block px-4 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <div className="font-medium text-slate-700 dark:text-slate-300 truncate">{row.name}</div>
                    <div className="text-xs text-slate-500 truncate">{row.email || row.company_name || row.industry || row.stage || ''}</div>
                  </a>
                ))}
                {(results[k] || []).length === 0 && (
                  <div className="px-4 py-3 text-xs text-slate-400">No matches</div>
                )}
              </div>
            </WidgetCard>
          ))}
        </div>
      )}
    </div>
  );
}

function CoachingTab() {
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ rep_id: '', note: '', is_best_practice: false });
  const [users, setUsers] = useState([]);
  const reload = useCallback(async () => {
    try {
      const [n, u] = await Promise.all([
        axios.get(`${API}/manage/coaching-notes`).catch(() => ({ data: { notes: [] } })),
        axios.get(`${API}/users`).catch(() => ({ data: [] })),
      ]);
      setNotes(n.data?.notes || []); setUsers(u.data || []);
    } catch {}
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/manage/coaching-notes`, form);
      setShowForm(false); setForm({ rep_id: '', note: '', is_best_practice: false });
      await reload();
    } catch {}
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Coaching notes & best practices</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Private feedback for reps and team-wide best-practice tags.</p>
        </div>
        <PrimaryButton onClick={() => setShowForm(true)} data-testid="coaching-add-btn">+ Add note</PrimaryButton>
      </div>

      {showForm && (
        <WidgetCard title="New coaching note" headerRight={<></>}>
          <form onSubmit={submit} className="space-y-3" data-testid="coaching-form">
            <select value={form.rep_id} onChange={(e) => setForm({ ...form, rep_id: e.target.value })} required className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100">
              <option value="">Select rep…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
            </select>
            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} required placeholder="Coaching note…" rows={3} className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100" />
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
              <input type="checkbox" checked={form.is_best_practice} onChange={(e) => setForm({ ...form, is_best_practice: e.target.checked })} />
              Mark as best practice (visible to whole team)
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300">Cancel</button>
              <PrimaryButton type="submit">Save</PrimaryButton>
            </div>
          </form>
        </WidgetCard>
      )}

      {notes.length === 0 ? (
        <WidgetCard title="Recent notes" headerRight={<></>}>
          <p className="text-sm text-slate-400">No coaching notes yet.</p>
        </WidgetCard>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div key={n.id} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 text-sm">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                <strong className="text-slate-700 dark:text-slate-300">{n.manager_name}</strong>
                <span>→ {users.find((u) => u.id === n.rep_id)?.name || n.rep_id?.slice(0, 8)}</span>
                {n.is_best_practice && <span className="ml-auto"><Pill tone="emerald">⭐ Best practice</Pill></span>}
              </div>
              <p className="text-slate-700 dark:text-slate-300">{n.note}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DelegationsTab() {
  const [data, setData] = useState({ granted: [], received: [] });
  const [showForm, setShowForm] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ to_user_id: '', expires_at: '', note: '' });
  const reload = useCallback(async () => {
    const [d, u] = await Promise.all([
      axios.get(`${API}/manage/delegations`).catch(() => ({ data: { granted: [], received: [] } })),
      axios.get(`${API}/users`).catch(() => ({ data: [] })),
    ]);
    setData(d.data); setUsers(u.data || []);
  }, []);
  useEffect(() => { reload(); }, [reload]);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/manage/delegations`, { ...form, expires_at: new Date(form.expires_at).toISOString() });
      setShowForm(false); setForm({ to_user_id: '', expires_at: '', note: '' });
      await reload();
    } catch {}
  };
  const revoke = async (id) => {
    await axios.delete(`${API}/manage/delegations/${id}`);
    await reload();
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Out-of-office deputy delegations</h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Temporarily grant a peer access to your team's data while you're away.</p>
        </div>
        <PrimaryButton onClick={() => setShowForm(true)} data-testid="delegation-add-btn">+ Delegate</PrimaryButton>
      </div>

      {showForm && (
        <WidgetCard title="New delegation" headerRight={<></>}>
          <form onSubmit={submit} className="space-y-3">
            <select value={form.to_user_id} onChange={(e) => setForm({ ...form, to_user_id: e.target.value })} required className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100">
              <option value="">Delegate to…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.role}</option>)}
            </select>
            <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} required className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100" />
            <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional note (e.g. 'On vacation 5–12 May')" className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 dark:text-slate-100" />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg text-slate-600 dark:text-slate-300">Cancel</button>
              <PrimaryButton type="submit">Save</PrimaryButton>
            </div>
          </form>
        </WidgetCard>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <WidgetCard title="Granted by me" headerRight={<></>}>
          {data.granted?.length === 0 ? (
            <p className="text-sm text-slate-400">None active</p>
          ) : (
            <div className="space-y-2 -mx-4">
              {data.granted?.map((d) => (
                <div key={d.id} className="px-4 py-2 border-t border-slate-100 dark:border-slate-700 first:border-0 flex items-start justify-between text-sm">
                  <div>
                    <div className="font-medium text-slate-700 dark:text-slate-300">{d.to_user_name}</div>
                    <div className="text-xs text-slate-500">until {new Date(d.expires_at).toLocaleString()}</div>
                    {d.note && <div className="text-xs italic text-slate-500 mt-1">{d.note}</div>}
                  </div>
                  <button onClick={() => revoke(d.id)} className="text-rose-600 text-xs hover:underline">Revoke</button>
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
        <WidgetCard title="Granted to me" headerRight={<></>}>
          {data.received?.length === 0 ? (
            <p className="text-sm text-slate-400">None active</p>
          ) : (
            <div className="space-y-2 -mx-4">
              {data.received?.map((d) => (
                <div key={d.id} className="px-4 py-2 border-t border-slate-100 dark:border-slate-700 first:border-0 text-sm">
                  <div className="font-medium text-slate-700 dark:text-slate-300">{d.from_user_name}</div>
                  <div className="text-xs text-slate-500">until {new Date(d.expires_at).toLocaleString()}</div>
                  {d.note && <div className="text-xs italic text-slate-500 mt-1">{d.note}</div>}
                </div>
              ))}
            </div>
          )}
        </WidgetCard>
      </div>
    </div>
  );
}

export default function ManagementHub() {
  const [tab, setTab] = useState('attention');
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="management-hub">
      {/* Page title — same hierarchy as /dashboard / /leads */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Zap className="w-6 h-6 text-amber-500" /> Management Hub
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Anomaly detection, manager reports, org-wide search, coaching, and delegations.
          </p>
        </div>
      </div>

      {/* Tabs — minimal underline style, matches /leads tab pattern */}
      <div className="border-b border-slate-200 dark:border-slate-700 flex gap-1 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 inline-flex items-center gap-2 whitespace-nowrap transition-colors ${
                active
                  ? 'border-slate-900 dark:border-white text-slate-900 dark:text-white'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              }`}
              data-testid={`mgmt-tab-${t.id}`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'attention' && <AttentionTab />}
      {tab === 'targets' && <TargetsTab />}
      {tab === 'reports' && <ReportsTab />}
      {tab === 'search' && <SearchTab />}
      {tab === 'coaching' && <CoachingTab />}
      {tab === 'delegations' && <DelegationsTab />}
    </div>
  );
}
